<script setup lang="ts">
import {
  ShopWsClientEvent,
  ShopWsInboundEvent,
  WebsocketService,
  parseShopWsInboundMessage,
  type OnlineShopCustomerPayload,
  type WebSocketMessage,
} from '@blueprint/frontend-basics'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { backendURL } from '@/helpers/config.helper.ts'
import { useServiceStore } from '@/stores/service.store.ts'

interface OnlineShopUser extends OnlineShopCustomerPayload {}

const props = defineProps<{
  shopId: string
}>()

const { t } = useI18n()
const serviceStore = useServiceStore()

const onlineUsers = ref<Map<string, OnlineShopUser>>(new Map())
const wsService = ref<WebsocketService | null>(null)
const connectionStatus = ref<
  'connected' | 'connecting' | 'disconnected' | 'lost'
>('disconnected')

const sortedUsers = computed(() =>
  Array.from(onlineUsers.value.values()).sort(
    (a, b) => b.connectedAt - a.connectedAt,
  ),
)

const setupWebSocket = (): void => {
  wsService.value?.disconnect()
  onlineUsers.value.clear()

  if (!props.shopId || !serviceStore.authService.isAuthenticated()) {
    return
  }

  wsService.value = new WebsocketService(backendURL, { role: 'business' })
  connectionStatus.value = 'connecting'

  wsService.value.onReady = () => {
    connectionStatus.value = 'connected'
    wsService.value?.sendShopMessage(ShopWsClientEvent.subscribeToShop, {
      shopId: props.shopId,
    })
  }

  wsService.value.onClose = () => {
    if (connectionStatus.value !== 'lost') {
      connectionStatus.value = 'connecting'
    }
  }

  wsService.value.onPermanentDisconnect = () => {
    connectionStatus.value = 'lost'
  }

  wsService.value.onMessage = (raw: WebSocketMessage) => {
    const message = parseShopWsInboundMessage(raw)
    if (!message) {
      return
    }
    switch (message.event) {
      case ShopWsInboundEvent.onlineCustomers: {
        onlineUsers.value.clear()
        for (const user of message.data) {
          onlineUsers.value.set(user.userId, user)
        }
        break
      }
      case ShopWsInboundEvent.customerJoined: {
        onlineUsers.value.set(message.data.userId, message.data)
        break
      }
      case ShopWsInboundEvent.customerLeft: {
        onlineUsers.value.delete(message.data.userId)
        break
      }
    }
  }

  wsService.value.connect(async () => {
    await serviceStore.authService.refreshToken()
    return serviceStore.authService.keycloak.token
  })
}

onMounted(() => setupWebSocket())

onUnmounted(() => {
  wsService.value?.disconnect()
})

watch(
  () => props.shopId,
  () => setupWebSocket(),
)
</script>

<template>
  <v-card variant="outlined" class="pa-4">
    <v-card-title class="text-h6 px-0">
      {{ t('shopManagement.onlineCustomers') }}
    </v-card-title>

    <div v-if="connectionStatus === 'connecting'" class="mb-3">
      <v-chip color="warning" size="small" variant="outlined">
        {{ t('shopManagement.reconnecting') }}
      </v-chip>
    </div>
    <div v-else-if="connectionStatus === 'lost'" class="mb-3">
      <v-chip color="error" size="small" variant="outlined" class="mb-2">
        {{ t('shopManagement.connectionLost') }}
      </v-chip>
      <v-btn size="small" variant="outlined" @click="setupWebSocket">
        {{ t('shopManagement.refreshPage') }}
      </v-btn>
    </div>

    <v-list v-if="sortedUsers.length > 0" density="comfortable">
      <v-list-item
        v-for="user in sortedUsers"
        :key="user.userId"
        :prepend-icon="user.icon"
        :title="user.userId"
        :subtitle="new Date(user.connectedAt).toLocaleTimeString()"
      />
    </v-list>

    <p v-else class="text-body-2 text-medium-emphasis">
      {{ t('shopManagement.noCustomersOnlineMessage') }}
    </p>
  </v-card>
</template>
